import type { DnaOverlay, DnaThreadRecord } from './api';

/**
 * Pure "which threads does the Refine screen actually show" decision — kept
 * framework-free (no React, no fetch), same house idiom as welcomeBack.ts/
 * quickStartGuide.ts/onboardingStep.ts, so the demo-vs-real source decision is
 * directly unit-testable rather than living only as an inline `?? null` in
 * Refine.tsx.
 *
 * THE DECISION THIS MODULE PINS (LANE W-CORPUS, wiring target #2): the REAL
 * Refine screen's "Threads Meira is noticing" card reads ONLY the served DNA
 * overlay's `profile.threads` (dna_profiles.threads — the era-to-era
 * continuity the synthesis found, GET /corpus/dna-overlay). It NEVER falls
 * back to the static placeholderRefine.threads pair
 * ('Cross-functional collaboration' / 'Mentorship & growth') that screen
 * rendered unconditionally before this lane. A user with no DNA-synthesis
 * pass yet (overlay.profile is null — loadOverlay's own honest "no
 * dna_profiles row" state) or a synthesis that found no continuity
 * (threads: null/[]) resolves to an empty result here, which the caller
 * renders as an honest nudge — never a fabricated stand-in.
 *
 * This is NOT the demo/onboarding screen's separate static threads chips
 * (placeholderHome.threadItems, Home.tsx) — that surface is explicitly out of
 * this lane's scope (Jason's own clarification: the DEMO/onboarding
 * placeholder stays; only the REAL, post-demo Refine screen gets wired here).
 */

/** Resolve the real threads to show, or null when there is genuinely no
 *  signal yet (no synthesis pass, or a pass that found no continuity). Never
 *  returns a fabricated/placeholder value — null IS the honest answer. */
export function resolveRealThreads(overlay: DnaOverlay | null): DnaThreadRecord[] | null {
  const threads = overlay?.profile?.threads ?? null;
  return threads && threads.length > 0 ? threads : null;
}

export type ThreadsDisplayState = 'loading' | 'real' | 'empty';

/** The three honest states the threads card switches on. `loading` while the
 *  overlay fetch is in flight; `real` once resolveRealThreads returns a
 *  non-empty array; `empty` otherwise (no pass yet, or nothing found) — the
 *  caller renders `empty` as a "keep talking with Meira" nudge, never as if it
 *  were the same thing as `real`. */
export function resolveThreadsDisplayState(
  loading: boolean,
  overlay: DnaOverlay | null,
): ThreadsDisplayState {
  if (loading) return 'loading';
  return resolveRealThreads(overlay) ? 'real' : 'empty';
}
